import { ActionTree } from 'vuex'

// @ts-ignore
import { SYSTEM_ERROR } from '@/common/const'
import services from '@/product/services'
import { QueryProductByPrintableSkuArgs } from '@/types/graphqlQuery'
import {
  ProductDetailModelFull,
  ProductState,
  PRODUCT_SELECTION_ERROR_TYPES
} from '@/product/types'
import { BQ_EVENT_NAMES, raksulBQEventTracker } from '@/common/utils/eventTracker'
import { getRemainingFastShippingQuantity } from '@/common/service'

const actions: ActionTree<ProductState, ProductState> = {
  async addToEstimate({ commit, state, getters }) {
    try {
      await services.addToEstimate({
        printableSkuId: state.productDetail.printableSkuId,
        addonPrintableSkuId: getters.addonProductPsi,
        sizes: state.sizeInput,
        skuPrintableCmykColorIds: state.productDetail.skuPrintableCmykColors.map(
          item => item.id
        ),
        skuPrintableSpecialColorIds: state.productDetail.skuPrintableSpecialColors.map(
          item => item.id
        ),
        optionIds: state.selectedAdditionalOptionIds.map(item => item)
      })
      commit('setAddToEstimate', true)
    } catch (error) {
      alert(SYSTEM_ERROR.UNKNOWN)
    }
  },
  initProductDetail({ commit, dispatch }, payload: any) {
    const { userDesignVersion, ...productByPrintableSku } = payload
    commit('setProductDetail', productByPrintableSku)
    dispatch('updateLimitOrderQuantity')
    if (userDesignVersion) commit('setStockLibraryDesign', userDesignVersion)
    const sizeInput = (productByPrintableSku as ProductDetailModelFull).estimate.sizeItems.map(
      size => {
        return {
          id: size.sizeId,
          quantity: size.quantity
        }
      }
    )
    commit('setSizeInput', sizeInput)
  },
  async getProductSkuData({ commit, dispatch }, payload: QueryProductByPrintableSkuArgs) {
    commit('setProductLoading', true)
    commit('setServerError', false)
    const data = await services
      .getProductSkuData(payload)
      .catch(() => {
        commit('setServerError', true)
      })
      .finally(() => {
        commit('setProductLoading', false)
      })
    if (data) {
      commit('updateProductNewSku', data)
      dispatch('updateLimitOrderQuantity')
    }
  },
  async getDeliveryDates({ commit }, payload: QueryProductByPrintableSkuArgs) {
    const data = await services.getDeliveryDates(payload)
    if (data) commit('setDeliveryDates', data.productByPrintableSku.estimate)
  },
  async updateEstimateInputs({ commit }, payload: QueryProductByPrintableSkuArgs) {
    commit('setProductLoading', true)
    commit('setServerError', false)
    const data = await services
      .changeEstimateInputs(payload)
      .catch(() => {
        commit('setServerError', true)
      })
      .finally(() => {
        commit('setProductLoading', false)
      })
    commit('updateEstimate', data)
  },
  showSizeInputWarning({ commit, dispatch, getters }) {
    if (!getters.validQuantity) {
      commit(
        'highlightSelectionError',
        PRODUCT_SELECTION_ERROR_TYPES.PRODUCT_SIZE
      )
    }
    dispatch('revealSnackbar')
  },
  showAddonWarning({ commit, dispatch, getters }) {
    if (getters.invalidProductSetSelection) {
      commit(
        'highlightSelectionError',
        PRODUCT_SELECTION_ERROR_TYPES.PRODUCT_SET
      )
    }
    dispatch('revealSnackbar')
  },
  showPrintingOptionsWarning({ commit, dispatch, getters }) {
    if (getters.invalidPrintingSelection) {
      commit(
        'highlightSelectionError',
        PRODUCT_SELECTION_ERROR_TYPES.PRINTING_OPTION
      )
    }
    dispatch('revealSnackbar')
  },
  revealSnackbar({ state, commit }) {
    if (!state.isMobileView) commit('setSnackbarVisibility', true)
  },
  showColorPatternSelectionWarning({ commit }) {
    commit('highlightSelectionError', PRODUCT_SELECTION_ERROR_TYPES.PRINTING_OPTION)
    commit('setSnackbarVisibility', true)
  },
  trackingError({ getters }, value: string) {
    const errorCode = (!getters.validQuantity || getters.invalidSizeSelection) ? 1
      : getters.invalidPrintingSelection ? 2
      : getters.invalidProductSetSelection ? 3 : 0
    raksulBQEventTracker({
      eventName: BQ_EVENT_NAMES.PRODUCT_DETAIL_PROCESS_ERROR,
      data: {
        button: value,
        errorCode
      }
    })
  },
  async updateLimitOrderQuantity({ getters, commit }) {
    let limit = -1
    if (getters.isFastShippingSelected) {
      const remainingQuantity = await getRemainingFastShippingQuantity(getters.psi)
      limit = remainingQuantity
    }
    commit('setMaxQuantity', limit)
  }
}

export default actions
